import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import WalkV2 from "@/pages/WalkV2";
import { toast } from "sonner";
import {
  Camera, CheckCircle, Circle, CaretRight, CaretLeft, House,
  Warning, PaperPlaneTilt, ClipboardText,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * TenantInspection — public, dummied-down self-inspection flow.
 * Route: /tenant-inspect/:token
 *
 * UX: room-by-room. Photos first (min 4), then a super-fast
 * "How's this room?" toggle + 3 preset Y/N questions, then Next.
 * No login. No 20-step wizards. Designed so a tenant with zero training
 * can finish in ~10 minutes on their phone.
 */
export default function TenantInspection() {
  const { token } = useParams();
  const [insp, setInsp] = useState(null);
  const [err, setErr] = useState(null);
  const [cursor, setCursor] = useState(0);        // room index
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [finalNote, setFinalNote] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/inspections/public/tenant/${token}`);
      setInsp(r.data);
      if (r.data.tenant_status === "submitted") setSubmitted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Link is invalid or expired");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (err) return <TenantErrorScreen err={err}/>;
  if (!insp) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (insp.walk_version === 2) return <WalkV2 mode="tenant" token={token}/>;
  if (submitted) return <TenantThanksScreen insp={insp}/>;

  const total = insp.rooms.length;
  const room = insp.rooms[cursor];
  const minPhotos = insp.min_photos_per_room;
  const roomsWithPhotos = insp.rooms.filter(r => r.photo_count >= minPhotos).length;

  const overallSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${BASE}/api/inspections/public/tenant/${token}/submit`,
        { final_note: finalNote });
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      {/* Header — big, friendly, mobile-first */}
      <div className="sticky top-0 z-30 bg-white border-b-2 border-slate-200">
        <div className="max-w-2xl mx-auto p-3 flex items-center gap-3">
          <House weight="fill" size={22} className="text-violet-600 flex-none"/>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-black uppercase tracking-widest text-violet-600">
              Self-Inspection · {(insp.kind || "").replace("_", "-")}
            </div>
            <div className="font-black text-slate-900 truncate">{insp.property_address}</div>
          </div>
          <div className="text-right flex-none">
            <div className="text-lg font-black tabular-nums text-violet-700">{roomsWithPhotos}/{total}</div>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">rooms done</div>
          </div>
        </div>
        <div className="h-1.5 bg-slate-100">
          <div className="h-full bg-violet-500 transition-all"
            style={{ width: `${Math.round(100 * roomsWithPhotos / (total || 1))}%` }}/>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Instruction card */}
        {cursor === 0 && !room.submitted && (
          <div className="mb-4 p-3 border-2 border-cyan-400 bg-cyan-50">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">👋 Welcome</div>
            <div className="text-sm text-slate-800 mt-1">
              Snap <b>at least {minPhotos} photos</b> of each room, tap how it looks, and move to
              the next. This takes about 10 minutes.
            </div>
          </div>
        )}

        {/* Room count nav */}
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setCursor(c => Math.max(0, c - 1))} disabled={cursor === 0}
            data-testid="tenant-prev-room"
            className="w-9 h-9 border-2 border-slate-300 disabled:opacity-30 flex items-center justify-center">
            <CaretLeft weight="bold" size={14}/>
          </button>
          <div className="flex-1 text-center">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Room {cursor + 1} of {total}</div>
            <div className="font-display font-black text-2xl">{room.name}</div>
          </div>
          <button onClick={() => setCursor(c => Math.min(total - 1, c + 1))} disabled={cursor === total - 1}
            data-testid="tenant-next-room"
            className="w-9 h-9 border-2 border-slate-300 disabled:opacity-30 flex items-center justify-center">
            <CaretRight weight="bold" size={14}/>
          </button>
        </div>

        <RoomBody
          key={cursor}
          token={token}
          room={room}
          minPhotos={minPhotos}
          onChange={() => load()}
        />
      </div>

      {/* Sticky footer — Next / Submit */}
      <div className="fixed bottom-0 left-0 right-0 border-t-2 border-slate-900 bg-white p-3 z-40">
        <div className="max-w-2xl mx-auto flex gap-2">
          {cursor < total - 1 ? (
            <button
              onClick={() => setCursor(c => c + 1)}
              disabled={room.photo_count < minPhotos}
              data-testid="tenant-next-btn"
              className="flex-1 h-12 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-1.5">
              {room.photo_count < minPhotos
                ? `Add ${minPhotos - room.photo_count} more photo${minPhotos - room.photo_count === 1 ? "" : "s"}`
                : (<>Next Room <CaretRight weight="bold" size={14}/></>)}
            </button>
          ) : (
            <button
              onClick={() => setShowFinal(true)}
              disabled={roomsWithPhotos < total}
              data-testid="tenant-review-btn"
              className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-1.5">
              {roomsWithPhotos < total
                ? `${total - roomsWithPhotos} room${total - roomsWithPhotos === 1 ? "" : "s"} still need photos`
                : (<><PaperPlaneTilt weight="fill" size={14}/> Review &amp; Submit</>)}
            </button>
          )}
        </div>
      </div>

      {showFinal && (
        <div className="fixed inset-0 z-[9999] bg-slate-950/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowFinal(false)} data-testid="tenant-final-modal">
          <div onClick={e => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg border-4 border-emerald-500 p-5 space-y-4 rounded-t-2xl sm:rounded-none">
            <h2 className="font-display font-black text-2xl">One last thing</h2>
            <div className="text-sm text-slate-600">
              Anything else your property manager should know? Broken items,
              recurring issues, notes on the exterior — anything goes.
            </div>
            <textarea value={finalNote} onChange={e => setFinalNote(e.target.value)}
              data-testid="tenant-final-note"
              placeholder="Optional — e.g., 'Front porch railing wobbles', 'Basement had a small leak in Feb.'"
              className="w-full h-32 p-3 border-2 border-slate-300 text-sm"/>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFinal(false)}
                className="flex-1 h-11 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-xs tracking-widest">
                Back
              </button>
              <button onClick={overallSubmit} disabled={submitting}
                data-testid="tenant-submit-btn"
                className="flex-[2] h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
                <PaperPlaneTilt weight="fill" size={14}/>
                {submitting ? "Submitting…" : "Submit Inspection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function RoomBody({ token, room, minPhotos, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [overall, setOverall] = useState(room.overall || null);
  const [quick, setQuick] = useState(room.quick || {});
  const [note, setNote] = useState(room.note || "");
  const saveTimer = useRef(null);

  const persist = async (extra = {}) => {
    try {
      await axios.patch(`${BASE}/api/inspections/public/tenant/${token}/room`, {
        room_idx: room.idx,
        overall: extra.overall ?? overall,
        quick: extra.quick ?? quick,
        note: extra.note ?? note,
      });
    } catch (e) { /* silent */ }
  };

  const scheduleSave = (extra) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(extra), 400);
  };

  const upload = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: arr.length });
    let done = 0;
    let failures = 0;
    const CONCURRENCY = 4;
    let next = 0;
    const worker = async () => {
      while (next < arr.length) {
        const idx = next++;
        const f = arr[idx];
        try {
          const fd = new FormData();
          fd.append("room_idx", String(room.idx));
          fd.append("file", f);
          await axios.post(`${BASE}/api/inspections/public/tenant/${token}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
          });
        } catch (e) {
          failures++;
        } finally {
          done++;
          setUploadProgress({ done, total: arr.length });
        }
      }
    };
    await Promise.all(new Array(Math.min(CONCURRENCY, arr.length)).fill(0).map(() => worker()));
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    if (failures) toast.error(`${failures} of ${arr.length} photo${arr.length === 1 ? "" : "s"} failed`);
    onChange?.();
  };

  const need = Math.max(0, minPhotos - room.photo_count);

  return (
    <div className="space-y-4">
      {/* Photo bucket */}
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-2">
          <Camera weight="fill" size={13}/> Photos of {room.name}
          <span className={`ml-auto tabular-nums ${room.photo_count >= minPhotos ? "text-emerald-700" : "text-amber-700"}`}>
            {room.photo_count} / {minPhotos}+
          </span>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="tenant-add-photos"
          className={`mt-1 w-full h-32 border-4 border-dashed flex flex-col items-center justify-center transition-colors ${
            need > 0 ? "border-cyan-400 bg-cyan-50 hover:bg-cyan-100" : "border-emerald-400 bg-emerald-50 hover:bg-emerald-100"
          } disabled:opacity-40`}>
          <Camera weight="fill" size={40} className={need > 0 ? "text-cyan-600" : "text-emerald-600"}/>
          <div className={`mt-1.5 font-black uppercase tracking-widest text-xs ${need > 0 ? "text-cyan-800" : "text-emerald-800"}`}>
            {uploading
              ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
              : need > 0 ? `Add ${need} more photo${need === 1 ? "" : "s"}` : "Add more (optional)"}
          </div>
          <div className="text-[10px] text-slate-600 mt-0.5">
            {uploading ? "Leave this page open until finished" : "Tap to open your camera or gallery · pick multiple at once"}
          </div>
        </button>
        <input ref={fileRef} type="file" multiple accept="image/*" capture="environment"
          className="hidden" data-testid="tenant-photo-input"
          onChange={(e) => { if (e.target.files?.length) { upload(e.target.files); e.target.value = ""; }}}/>
        {room.photos.length > 0 && (
          <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid="tenant-photo-grid">
            {room.photos.map((p, k) => (
              <div key={k} className="aspect-square border border-slate-200 overflow-hidden bg-slate-100">
                <img src={`${BASE}/api/inspections/walk-photo/${p.path}?t=${encodeURIComponent(token)}`}
                  alt="" className="w-full h-full object-cover"/>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Overall toggle */}
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">How's this room?</div>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <button onClick={() => { setOverall("good"); scheduleSave({ overall: "good" }); }}
            data-testid="tenant-overall-good"
            className={`h-14 border-2 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 ${
              overall === "good" ? "bg-emerald-600 border-emerald-700 text-white"
                                 : "bg-white border-slate-300 text-slate-700 hover:border-emerald-500"
            }`}>
            <CheckCircle weight="fill" size={16}/> Looks Good
          </button>
          <button onClick={() => { setOverall("issue"); scheduleSave({ overall: "issue" }); }}
            data-testid="tenant-overall-issue"
            className={`h-14 border-2 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 ${
              overall === "issue" ? "bg-amber-600 border-amber-700 text-white"
                                  : "bg-white border-slate-300 text-slate-700 hover:border-amber-500"
            }`}>
            <Warning weight="fill" size={16}/> Has an Issue
          </button>
        </div>
      </div>

      {/* Three quick Y/N/N-A */}
      <div className="space-y-2">
        {["floors", "walls", "appliances"].map(k => (
          <div key={k} className="border-2 border-slate-200 bg-white p-3">
            <div className="text-sm font-bold text-slate-800">
              {k === "floors" ? "Floors look OK?"
                : k === "walls" ? "Walls & ceilings look OK?"
                : "Appliances / fixtures work?"}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {["yes", "no", "na"].map(v => (
                <button key={v}
                  onClick={() => { const nq = { ...quick, [k]: v }; setQuick(nq); scheduleSave({ quick: nq }); }}
                  data-testid={`tenant-q-${k}-${v}`}
                  className={`h-10 border-2 font-black uppercase text-[10px] tracking-widest ${
                    quick[k] === v
                      ? v === "yes" ? "bg-emerald-600 border-emerald-700 text-white"
                        : v === "no" ? "bg-rose-600 border-rose-700 text-white"
                        : "bg-slate-600 border-slate-700 text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
                  }`}>
                  {v === "na" ? "N/A" : v.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Note */}
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes (optional)</label>
        <textarea value={note}
          onChange={e => { setNote(e.target.value); scheduleSave({ note: e.target.value }); }}
          data-testid="tenant-room-note"
          placeholder={overall === "issue" ? "Describe the issue — e.g., 'chip on hardwood near door'" : "Anything worth mentioning about this room…"}
          className="mt-1 w-full h-20 p-2 border-2 border-slate-300 text-sm"/>
      </div>
    </div>
  );
}


function TenantErrorScreen({ err }) {
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full border-4 border-rose-500 bg-slate-900 p-6 text-center">
        <Warning weight="fill" size={44} className="mx-auto text-rose-500"/>
        <h1 className="mt-3 font-display font-black text-2xl">Link Unavailable</h1>
        <p className="mt-2 text-sm text-slate-300">{err}</p>
        <p className="mt-4 text-xs text-slate-500">
          Please contact your property manager for a fresh link.
        </p>
      </div>
    </div>
  );
}


function TenantThanksScreen({ insp }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-600 to-cyan-700 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
          <CheckCircle weight="fill" size={64} className="text-white"/>
        </div>
        <h1 className="mt-6 font-display font-black text-4xl uppercase tracking-tight">Thank you!</h1>
        <p className="mt-3 text-lg opacity-90">
          Your self-inspection for <b>{insp.property_address}</b> is submitted.
        </p>
        <p className="mt-2 text-sm opacity-80">
          Your property manager will review the photos and follow up on any
          maintenance items. You can close this page.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-70">
          <ClipboardText weight="fill" size={12}/> Powered by Field Radar
        </div>
      </div>
    </div>
  );
}
